// src/components/ObjectSubNav.jsx
//
// OBJ-SUBNAV-LINE — Generic four-slot sub-nav tab bar shared by
// Locations, Leases, and Projects landing pages.
//
// Matches the Projects ModuleTabBar visual spec exactly:
//   flex gap 24, borderBottom 1px T.border, active tab blue underline,
//   amber badge pill (T.utilAmber / T.amberDark / T.decisionsCardBorder).
//
// Props:
//   tabs         — Array<{ key: string, label: string }>  (display order)
//   activeTab    — key of the currently active tab
//   onChange     — (key: string) => void
//   badgeTab     — key of the tab that carries the badge count (slot 2)
//   badgeCount   — number; badge only rendered when > 0
//   testIdPrefix — string prefix for data-testid attributes
//                  e.g. "locations" → data-testid="locations-subnav-tab-portfolio"
//   endSlot      — optional ReactNode rendered right-aligned on the tab row
//   disabledTabs — optional array of tab keys to render as dimmed, non-interactive
//                  (no onClick, no active-underline). Used for "· Phase 2" ghost tabs.

import React from "react";
import { T } from "../app/tokens.js";
import "./objectChrome.css";

export function ObjectSubNav({
  tabs,
  activeTab,
  onChange,
  badgeTab,
  badgeCount = 0,
  badges = {},
  testIdPrefix = "object",
  endSlot,
  disabledTabs = [],
}) {
  return (
    <div
      data-testid={`${testIdPrefix}-subnav`}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        alignContent: "flex-end",
        gap: 24,
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      {tabs.map((t) => {
        const isDisabled = disabledTabs.includes(t.key);
        const count = badges[t.key] ?? (t.key === badgeTab ? badgeCount : 0);
        if (isDisabled) {
          return (
            <span
              key={t.key}
              data-testid={`${testIdPrefix}-subnav-tab-${t.key}`}
              aria-disabled="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "0 2px 12px",
                background: "none",
                border: "none",
                borderBottom: "2px solid transparent",
                marginBottom: -1,
                fontSize: T.fsMd,
                fontWeight: T.fwSemibold,
                color: T.light,
                cursor: "default",
              }}
            >
              {t.label}
            </span>
          );
        }
        return (
          <button
            key={t.key}
            type="button"
            className="object-subnav-tab"
            data-active={activeTab === t.key ? "true" : "false"}
            data-testid={`${testIdPrefix}-subnav-tab-${t.key}`}
            onClick={() => onChange(t.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "0 2px 12px",
              border: "none",
              borderBottom: `2px solid ${activeTab === t.key ? T.blue : "transparent"}`,
              marginBottom: -1,
              fontSize: T.fsMd,
              fontWeight: T.fwSemibold,
              "--object-tab-ink": activeTab === t.key ? T.blue : T.mid,
              "--object-tab-accent": T.blue,
              "--object-tab-hover-bg": T.listChipBg,
              cursor: "pointer",
            }}
          >
            {t.label}
            {count > 0 && (
              <span
                data-testid={`${testIdPrefix}-subnav-badge${t.key === badgeTab ? "" : `-${t.key}`}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                  padding: "2px 6px",
                  minWidth: 18,
                  fontSize: T.fsXxs,
                  fontWeight: T.fwSemibold,
                  lineHeight: 1,
                  textAlign: "center",
                  background: T.utilAmber,
                  color: T.amberDark,
                  whiteSpace: "nowrap",
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
      {endSlot && (
        <div style={{ marginTop: 4, paddingBottom: 8 }}>
          {endSlot}
        </div>
      )}
    </div>
  );
}
